import { useEffect } from "react";

/*
 * Section snapping with a soft hand — one continuous glide.
 *
 * The primary inputs (mouse wheel, paging keys) are intercepted before
 * the browser scrolls, and the whole travel to the next stop is a single
 * eased animation — never the native jump followed by a snap of the
 * remainder. Touch scrolling keeps its native feel and settles onto the
 * nearest stop after the gesture ends. Dragging the browser scrollbar
 * suspends snapping entirely: a settle mid-drag would fight the thumb,
 * and the released position is exactly where the user asked to be.
 *
 * Stops: every main section except the solutions wrapper (the product
 * groups snap by their header line under the masthead), plus career's
 * heading on phones. Snapping is desktop-only (>1024px): phones and the
 * stacked in-between band scroll natively. The down-cue and in-page
 * anchors still glide on phones — those are explicit taps, not snaps.
 */
export function useSectionSnap() {
  useEffect(() => {
    const phone = window.matchMedia("(max-width: 768px)");
    const stacked = window.matchMedia("(min-width: 769px) and (max-width: 1024px)");

    let raf = 0;
    let idle = 0;
    let lastY = window.scrollY;
    let dir = 0;
    let animating = false;
    let glideTarget = 0;
    let lastStep = 0;

    const maxScroll = () =>
      document.documentElement.scrollHeight - window.innerHeight;

    /* pinch-zoomed on iOS: the visual pan lives in scrollX/Y — snapping
       (and its scrollTo x=0) would yank the zoomed view to the left
       edge, so the snapper stands down entirely while zoomed */
    const zoomed = () =>
      (window.visualViewport ? window.visualViewport.scale : 1) > 1.02;

    interface Stop { y: number; el: Element | null; off: number; }
    const stops = (): Stop[] => {
      const isPhone = phone.matches;
      const headerOff = isPhone ? 70 : 80;
      const groupOff = isPhone ? 134 : 190;
      const list: Stop[] = [];
      const add = (el: Element | null, off: number) => {
        if (el) {
          list.push({
            y: el.getBoundingClientRect().top + window.scrollY - off,
            el,
            off,
          });
        }
      };
      document.querySelectorAll("main > section").forEach((s) => {
        if (s.id === "our-solutions") return;
        if (isPhone && s.id === "why-to-join") return; /* snaps by its h1 */
        add(s, headerOff);
      });
      document.querySelectorAll(".examples-group-header").forEach((g) =>
        add(g, groupOff)
      );
      if (isPhone) {
        add(document.querySelector(".section-career h2"), 92);
      }
      /* the page end is a legitimate stop */
      list.push({ y: maxScroll(), el: null, off: 0 });
      return list.sort((a, b) => a.y - b.y);
    };

    /* ease-out: motion begins the instant the input lands, then decays
       into a soft landing — no dead time at the start */
    const ease = (t: number) => 1 - Math.pow(1 - t, 3);

    const glide = (stop: { el: Element | null; off: number; y: number }) => {
      /* the target is re-read from the element every frame: on a fresh
         page the reveal transforms are still shifting layout, and a
         frozen number lands off by that shift */
      const liveTarget = () => {
        const raw = stop.el
          ? stop.el.getBoundingClientRect().top + window.scrollY - stop.off
          : stop.y;
        return Math.max(0, Math.min(maxScroll(), raw));
      };
      /* the destination's entrance completes instantly at glide start:
         letting its 0.8s translateY transition run would keep moving the
         tracked target mid-flight, which reads as lag */
      const rev = stop.el?.closest(".reveal") as HTMLElement | null;
      if (rev && !rev.classList.contains("visible")) {
        rev.style.transition = "none";
        rev.classList.add("visible");
        void rev.offsetHeight; /* commit before restoring the transition */
        rev.style.transition = "";
      }
      const from = window.scrollY;
      const to0 = liveTarget();
      if (Math.abs(to0 - from) < 2) return;
      cancelAnimationFrame(raf);
      animating = true;
      glideTarget = to0;
      /* duration scales gently with distance — long travels stay calm,
         short corrections stay brief */
      const dur = Math.min(3000, 1100 + Math.abs(to0 - from) * 1.5);
      const t0 = performance.now();
      /* the page's scroll-behavior: smooth would turn every frame into
         its own laggy animation — and Safari doesn't reliably accept
         behavior: "instant", so override the CSS for the glide instead */
      const root = document.documentElement;
      const prevBehavior = root.style.scrollBehavior;
      root.style.scrollBehavior = "auto";
      const step = (now: number) => {
        const t = Math.min(1, (now - t0) / dur);
        const to = liveTarget();
        glideTarget = to;
        window.scrollTo(window.scrollX, from + (to - from) * ease(t));
        if (t < 1) raf = requestAnimationFrame(step);
        else {
          animating = false;
          lastY = window.scrollY;
          root.style.scrollBehavior = prevBehavior;
        }
      };
      raf = requestAnimationFrame(step);
    };

    /* step to the neighboring stop — from the in-flight target while a
       glide is running, so repeated inputs chain smoothly */
    const stepStop = (d: 1 | -1) => {
      const now = performance.now();
      if (now - lastStep < 260) return;
      lastStep = now;
      const y = animating ? glideTarget : window.scrollY;
      const ss = stops();
      let target: (typeof ss)[number] | null = null;
      if (d > 0) target = ss.find((s) => s.y > y + 6) ?? null;
      else {
        for (const s of ss) if (s.y < y - 6) target = s;
      }
      if (target === null) return;
      glide(target);
    };

    /* ---- wheel: one flick, one continuous glide ----
       Mac trackpads keep emitting a decaying momentum stream after the
       flick; without inertia detection a single swipe steps several
       sections. After each step the wheel disarms until the stream
       pauses or a delta clearly larger than the decaying tail arrives
       (a genuinely new flick). */
    let acc = 0;
    let accT = 0;
    let armed = true;
    let tailPeak = 0;
    let lastWheelT = 0;
    const onWheel = (e: WheelEvent) => {
      if (phone.matches || stacked.matches || zoomed()) return;
      if (e.ctrlKey) return; /* pinch-zoom gesture */
      e.preventDefault();
      const now = performance.now();
      const gap = now - lastWheelT;
      lastWheelT = now;
      const mag = Math.abs(e.deltaY);
      if (!armed) {
        tailPeak = Math.max(tailPeak * 0.985, 1);
        if (gap > 300 || mag > Math.max(48, tailPeak * 1.5)) {
          armed = true;
        } else {
          return; /* still the old gesture's momentum */
        }
      }
      acc += e.deltaY;
      window.clearTimeout(accT);
      accT = window.setTimeout(() => { acc = 0; }, 220);
      /* a deliberate flick, not a grazing tick */
      if (Math.abs(acc) < 110) return;
      const d = acc > 0 ? 1 : -1;
      acc = 0;
      armed = false;
      tailPeak = mag;
      stepStop(d);
    };

    /* ---- paging keys: same continuous glide ---- */
    const onKey = (e: KeyboardEvent) => {
      if (phone.matches || stacked.matches || zoomed()) return;
      const t = e.target as HTMLElement | null;
      if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
      const k = e.key;
      if (k === "Home") {
        e.preventDefault();
        glide({ el: null, off: 0, y: 0 });
        return;
      }
      if (k === "End") {
        e.preventDefault();
        glide({ el: null, off: 0, y: maxScroll() });
        return;
      }
      let d: 1 | -1 | 0 = 0;
      if (k === "PageDown" || k === "ArrowDown") d = 1;
      else if (k === "PageUp" || k === "ArrowUp") d = -1;
      else if (k === " ") d = e.shiftKey ? -1 : 1;
      if (!d) return;
      e.preventDefault();
      stepStop(d);
    };

    /* ---- in-page anchors (hero cue, header nav) ride the same glide —
       the browser's native smooth scroll has its own speed and the two
       side by side read as inconsistent ---- */
    const onClick = (e: MouseEvent) => {
      if (zoomed()) return;
      const t = e.target as HTMLElement | null;
      const a = t?.closest?.('a[href^="#"]') as HTMLAnchorElement | null;
      if (!a) return;
      const id = a.getAttribute("href")!.slice(1);
      /* the header wordmark: back to the very top */
      if (id === "top" || id === "") {
        e.preventDefault();
        glide({ el: null, off: 0, y: 0 });
        return;
      }
      const el = id ? document.getElementById(id) : null;
      if (!el) return;
      e.preventDefault();
      const off = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
      glide({ el, off, y: 0 });
    };

    /* ---- scrollbar drag: the snapper stands down ----
       A mousedown landing to the right of the layout viewport is on the
       vertical scrollbar track (or, with overlay scrollbars, on the root
       element at the window's edge). Any glide in flight is cut and no
       settle runs until the button is released. */
    let scrollbarDrag = false;
    const stopGlide = () => {
      window.clearTimeout(idle);
      if (animating) {
        cancelAnimationFrame(raf);
        animating = false;
        lastY = window.scrollY;
        document.documentElement.style.scrollBehavior = "";
      }
    };
    const onScrollbarDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      const root = document.documentElement;
      const onTrack =
        e.clientX >= root.clientWidth ||
        e.clientY >= root.clientHeight ||
        (e.target === root && window.innerWidth - e.clientX <= 20);
      if (!onTrack) return;
      scrollbarDrag = true;
      stopGlide();
    };
    const onScrollbarUp = () => {
      if (!scrollbarDrag) return;
      scrollbarDrag = false;
      /* released — stay put; no after-drag settle */
      window.clearTimeout(idle);
      lastY = window.scrollY;
    };
    /* a release outside the window never reaches mouseup */
    const onMove = (e: MouseEvent) => {
      if (scrollbarDrag && e.buttons === 0) onScrollbarUp();
    };

    /* ---- fallback settle for touch scrolling ---- */
    const settle = () => {
      /* phones scroll natively — no after-gesture settle onto stops */
      if (animating || scrollbarDrag) return;
      if (phone.matches || stacked.matches || zoomed()) return;
      const y = window.scrollY;
      if (y >= maxScroll() - 4) return;
      let best: { y: number; el: Element | null; off: number } | null = null;
      let bestW = Infinity;
      for (const s of stops()) {
        const d = s.y - y;
        const against = (dir < 0 && d > 4) || (dir > 0 && d < -4);
        const w = Math.abs(d) * (against ? 2.2 : 1);
        if (w < bestW) { bestW = w; best = s; }
      }
      if (best === null || Math.abs(best.y - y) < 2) return;
      glide(best);
    };

    const onScroll = () => {
      if (animating) return;
      const y = window.scrollY;
      if (Math.abs(y - lastY) > 1) dir = y > lastY ? 1 : -1;
      lastY = y;
      window.clearTimeout(idle);
      if (scrollbarDrag) return;
      idle = window.setTimeout(settle, 150);
    };

    /* a fresh touch interrupts a glide instantly */
    const onTouchStart = () => stopGlide();

    /* the global down-cue asks for one step through this event */
    const onStepDown = () => {
      if (stacked.matches || zoomed()) return;
      stepStop(1);
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("keydown", onKey);
    document.addEventListener("click", onClick);
    window.addEventListener("daalab:step-down", onStepDown);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("mousedown", onScrollbarDown, true);
    window.addEventListener("mouseup", onScrollbarUp, true);
    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("blur", onScrollbarUp);
    return () => {
      window.clearTimeout(idle);
      window.clearTimeout(accT);
      cancelAnimationFrame(raf);
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("click", onClick);
      window.removeEventListener("daalab:step-down", onStepDown);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("mousedown", onScrollbarDown, true);
      window.removeEventListener("mouseup", onScrollbarUp, true);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("blur", onScrollbarUp);
    };
  }, []);
}
